import React, { useCallback } from 'react'
import { ReactComponent as GoogleLogo } from './../../images/google_ads_logo.svg'
import { ReactComponent as GA4Logo } from './../../images/ga4_logo.svg'
import { ReactComponent as FacebookLogo } from './../../images/facebook_logo.svg'
import { ReactComponent as BrevoLogo } from './../../images/vendors/brevo_logo.svg'
import { openSignInWindow } from '../../lib/utils/openSigninWindow'
import { Vendor } from '../../lib/types/Vendor'
import dayjs from 'dayjs'
import { ReactComponent as GoogleLightNormal } from './../../images/icons/btn_google_light_normal_ios.svg'
import { ReactComponent as EleadLogo } from './../../images/vendors/elead_logo.svg'
import { ReactComponent as TradeDeskLogo } from './../../images/vendors/trade_desk_logo.svg'
import { Button } from '@demandlocal/components'
import { getImageURL } from '../../lib/utils/getImageURL'
import { VENDOR } from '../../lib/utils/vendors'
import { useDealership } from '../../store/zustand/dealershipStore'
import { useModal } from '../useModal'
import ConnectCentroForm from '../../forms/LinkedAccounts/ConnectCentro'
import ConnectBrevoForm from '../../forms/LinkedAccounts/ConnectBrevo'
import { match, P } from 'ts-pattern'

type Props = {
  vendor: Vendor
  disabled?: boolean
}

const VendorAccount = ({ vendor, disabled }: Props): JSX.Element => {
  const dealership = useDealership()
  const { handleModal } = useModal()

  const getButtonForVendor = useCallback(() => {
    if (vendor.id === VENDOR.TradeDesk) {
      return (
        <Button
          variant={'secondary'}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault()
            location.href = 'mailto:integrations@link1data.com'
          }}>
          Contact us
        </Button>
      )
    }

    if (vendor.id === VENDOR.AmazonCognition) {
      return (
        <Button
          variant={'secondary'}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault()
            location.href = 'mailto:integrations@link1data.com'
          }}>
          Contact us
        </Button>
      )
    }

    if (vendor.id === VENDOR.Centro) {
      return (
        <Button
          variant={'primary'}
          disabled={disabled}
          onClick={(e) => {
            e.preventDefault()
            handleModal(<ConnectCentroForm />)
          }}>
          Connect Basis
        </Button>
      )
    }
    if (vendor.id === VENDOR.Brevo) {
      return (
        <Button
          variant={'primary'}
          onClick={(e) => {
            e.preventDefault()
            handleModal(<ConnectBrevoForm />)
          }}>
          Connect Brevo
        </Button>
      )
    }

    if (vendor.name === 'Google Ads' || vendor.id === VENDOR.GoogleAnalytics) {
      return (
        <button
          onClick={(e) => {
            e.preventDefault()
            openSignInWindow(
              `${import.meta.env.VITE_API_URL}${String(
                vendor.api_oauth_endpoint,
              )}?current_organization_id=${dealership?.organization_id}&scope=${
                vendor.required_scopes
              }&vendor_id=${vendor.id}`,
              'test',
            )
          }}
          className={
            'flex items-center justify-center bg-white active:bg-gray-50 pr-2 drop-shadow-md rounded-sm text-sm font-Roboto text-black/54'
          }
          disabled={disabled}>
          <GoogleLightNormal />
          Sign in with Google
        </button>
      )
    }

    if (vendor.name === 'Facebook Ads') {
      return (
        <button
          onClick={(e) => {
            e.preventDefault()
            openSignInWindow(
              `${import.meta.env.VITE_API_URL}${String(
                vendor.api_oauth_endpoint,
              )}?current_organization_id=${dealership?.organization_id}&scope=${
                vendor.required_scopes
              }&vendor_id=${vendor.id}`,
              'test',
            )
          }}
          className={
            'flex items-center justify-center font-bold active:bg-gray-50 drop-shadow-md rounded-sm py-2 px-4 text-sm text-white bg-blue-crayola cursor-pointer'
          }
          disabled={disabled}>
          <FacebookLogo fill={'#FFF '} className="mr-2" />
          Login in with Facebook
        </button>
      )
    }
    return (
      <Button
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault()
          openSignInWindow(
            `${import.meta.env.VITE_API_URL}${String(
              vendor.api_oauth_endpoint,
            )}?current_organization_id=${dealership?.organization_id}&vendor_id=${
              vendor.id
            }`,
            'test',
          )
        }}>
        Connect {vendor.name}
      </Button>
    )
  }, [
    dealership?.organization_id,
    handleModal,
    vendor.api_oauth_endpoint,
    vendor.id,
    vendor.name,
    vendor.required_scopes,
    disabled,
  ])

  return (
    <div
      className={
        'flex flex-col justify-center items-center bg-gray-100 py-10 px-10 font-Spartan rounded-xl'
      }>
      <div className={'flex font-bold justify-center items-center text-sm'}>
        {vendor.name}
      </div>
      {vendor.name === 'Google Ads' && <GoogleLogo className={'w-16 pt-5'} />}
      {vendor.name === 'Bing Ads' && (
        <img src={getImageURL('images/bing_ads_logo.png')} className={'w-16 pt-5'} />
      )}
      {vendor.name === 'Facebook Ads' && (
        <img src={getImageURL('images/facebook_ads_logo.png')} className={'w-16 pt-5'} />
      )}
      {vendor.name === 'Mailchimp' && (
        <img src={getImageURL('images/mailchimp_logo.png')} className={'w-40 pt-5'} />
      )}
      {vendor.id === VENDOR.Centro && (
        <img src={getImageURL('images/vendors/basis_logo.svg')} className={'w-40 pt-5'} />
      )}
      {vendor.name === 'Elead' && <EleadLogo />}
      {vendor.id === VENDOR.TradeDesk && (
        <TradeDeskLogo className={'object-center w-48 h-16'} />
      )}
      {vendor.id === VENDOR.Brevo && <BrevoLogo className={'w-40 pt-5'} />}
      {vendor.id === VENDOR.AmazonCognition && (
        <img
          src={getImageURL('images/vendors/amazon_ads_logo.png')}
          className={'w-40 pt-5'}
        />
      )}
      {vendor.id === VENDOR.HubSpot && (
        <img
          src={getImageURL('images/vendors/hubspot_logo.png')}
          className={'w-16 pt-5'}
        />
      )}
      {vendor.id === VENDOR.GoogleAnalytics && <GA4Logo className={'w-40 pt-5'} />}
      <div className={'pt-6 flex flex-row'}>
        {match(vendor)
          .with(
            {
              connected: true,
              connectionDetails: {
                expired_at: P.nullish,
              },
            },
            () => (
              <div className={'flex flex-col items-center'}>
                <div className={'cursor-not-allowed'}>
                  <Button disabled={true}>Connected</Button>
                </div>
                <div className={'pt-2 font-medium text-xs text-primary-persian-blue'}>
                  {`Connected ${vendor.id === VENDOR.Centro ? 'by' : 'with'}  ${
                    vendor.connectionDetails?.user_name
                  } on ${dayjs(vendor.connectionDetails?.created_date).format(
                    'MM/DD/YYYY',
                  )}`}
                </div>
              </div>
            ),
          )
          .with(
            {
              connected: true,
              connectionDetails: {
                expired_at: P.not(P.nullish),
              },
            },
            () => (
              <div className={'flex flex-col items-center'}>
                {getButtonForVendor()}
                <div
                  className={'pt-2 font-medium text-xs text-secondary-engineering-red'}>
                  Token expired
                </div>
              </div>
            ),
          )
          .with(
            {
              connected: false,
            },
            () => getButtonForVendor(),
          )
          .otherwise(() => (
            <></>
          ))}
      </div>
      {vendor.id === VENDOR.AmazonCognition || vendor.id === VENDOR.TradeDesk ? (
        <div className={'mt-4 text-xs font-bold'}>
          * Ads must be purchased via LinkOne Professional Services
        </div>
      ) : null}
      {vendor.id === VENDOR.Centro && (
        <div className={'mt-4 text-xs font-bold'}>
          * Does not work with "Basis Platform"
        </div>
      )}
    </div>
  )
}

export default VendorAccount
